import { useEffect, useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

// Illustrative only — not wired to real Firestore activity. Do not relabel as "Live"/"Real"
// until this reads from actual payments/fund pages; see docs/PRODUCT_CHANGELOG.md 2026-08-15.
const ACTIVITY = [
    { who: "Marcus", action: "gave $45", prep: "to", student: "Priya", track: "CS @ NYU", emoji: "💙" },
    { who: "Auntie Lola", action: "started $40/mo", prep: "for", student: "Devon", track: "Welding @ TSTC", emoji: "🛠️" },
    { who: "Pastor Ray", action: "gave $100", prep: "to", student: "Maria", track: "Env. Policy @ Berkeley", emoji: "🌿" },
    { who: "Grandpa Joe", action: "started $75/mo", prep: "for", student: "Esme", track: "Architecture @ Cooper Union", emoji: "📐" },
    { who: "Coach Mendez", action: "gave $60", prep: "to", student: "Tariq", track: "Pre-med @ Morehouse", emoji: "⚕️" },
    { who: "Book Club", action: "gave $200", prep: "to", student: "Riley", track: "Music Ed @ Berklee", emoji: "🎶" },
    { who: "Mom's coworker", action: "gave $50", prep: "to", student: "Sade", track: "Public Health @ Spelman", emoji: "🌍" },
    { who: "Uncle Ben", action: "started $50/mo", prep: "for", student: "Jay", track: "Diesel Tech @ TSTC", emoji: "🚛" },
];

const LiveActivitySection = () => {
    const { ref, isVisible } = useScrollAnimation(0.05);
    const [tick, setTick] = useState(0);

    // gently rotate the highlighted row to feel live
    useEffect(() => {
        const id = setInterval(() => setTick((t) => (t + 1) % ACTIVITY.length), 2500);
        return () => clearInterval(id);
    }, []);

    return (
        <section
            ref={ref}
            className="py-16 md:py-20 bg-white"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div
                    className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 transition-all duration-700 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                    }`}
                >
                    <div>
                        <div className="inline-flex items-center gap-2 mb-3">
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slate-400" />
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Example
                            </span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-primary leading-tight tracking-tight">
                            Here's what backing a student looks like.
                        </h2>
                        <p className="text-slate-600 mt-3 max-w-xl">
                            A preview of the kind of support a shared Fund Page brings in — this is what shows up when one person shares a page in a family group chat.
                        </p>
                    </div>
                    <div className="flex items-center gap-6">
                        <div>
                            <div className="text-3xl md:text-4xl font-extrabold text-primary leading-none">
                                Free
                            </div>
                            <div className="text-xs md:text-sm text-slate-500 mt-1">
                                always, for students
                            </div>
                        </div>
                        <div>
                            <div className="text-3xl md:text-4xl font-extrabold leading-none" style={{ color: "hsl(var(--gold))" }}>
                                Minutes
                            </div>
                            <div className="text-xs md:text-sm text-slate-500 mt-1">
                                to set up
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={`rounded-3xl border border-slate-200/70 bg-gradient-to-br from-white to-slate-50 overflow-hidden shadow-sm transition-all duration-700 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                    }`}
                >
                    <ul className="divide-y divide-slate-100">
                        {ACTIVITY.map((row, i) => {
                            const isActive = i === tick;
                            return (
                                <li
                                    key={`${row.who}-${i}`}
                                    className={`flex items-center gap-4 px-5 md:px-7 py-4 transition-colors duration-500 ${
                                        isActive ? "bg-emerald-50/60" : "bg-transparent"
                                    }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center text-lg flex-shrink-0">
                                        {row.emoji}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm md:text-base text-slate-800 truncate">
                                            <span className="font-semibold">{row.who}</span>{" "}
                                            <span className="text-emerald-700 font-semibold">{row.action}</span>{" "}
                                            <span className="text-slate-500">{row.prep}</span>{" "}
                                            <span className="font-medium text-primary">{row.student}</span>
                                        </p>
                                    </div>
                                    <div className="text-xs md:text-sm text-slate-500 flex-shrink-0 font-medium">
                                        {row.track}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default LiveActivitySection;
